import {
  Building2,
  Clock,
  Users,
  Megaphone,
  IdCard,
  Landmark,
  Layers,
  Shield,
  ListOrdered,
  Medal,
  HardDrive,
  Grid3x3,
  HardHat,
  Blocks,
  ClipboardList,
  FileSignature,
  Flag,
  Handshake,
  Waypoints,
  ScanLine,
  Settings2,
  Spline,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import { sectionRoot } from './place-path';
import type { StaffRole } from '../types/user';

/**
 * The kinds of place the panel manages.
 *
 * `trophy` is a circuit — no walls, no opening hours; it has a roster and a
 * list of stages. `competition` is one event: a stage of a circuit, or a gym's
 * own — walls to set (or borrow) and an entry list. Which of the two it is,
 * and whether a gym hosts it, is the `PlaceShape` below rather than a fifth
 * kind: every service already keys its URLs on these four.
 */
export type ManagedLocationType = 'gym' | 'crag' | 'trophy' | 'competition';

/**
 * What a competition hangs off, which decides a few entries on its own.
 *
 * A stage borrows its categories and teams from the circuit, so it has no
 * section for them; a gym's own event keeps its own. One held at a gym borrows
 * the gym's climbing area, so it has nothing of its own to configure. Both
 * flags are false for a gym, a crag and a trophy, and for a competition that
 * is nobody's.
 */
export interface PlaceShape {
  /** A competition of a circuit: its categories and teams are the trophy's. */
  stage: boolean;
  /** A competition held at a gym: its zones and hold types are the gym's. */
  hostedAtVenue: boolean;
}

export const PLAIN_PLACE: PlaceShape = { stage: false, hostedAtVenue: false };

/**
 * Sidebar navigation for the gym-admin app. `roles` lists the staff roles that
 * may see and open each entry, `locations` the kinds of place it applies to.
 * This single source of truth drives the sidebar, the route guards and the
 * default landing redirect.
 */
export interface GymAdminNavItem {
  /** Route path relative to the gym-admin root. */
  path: string;
  /** i18n key under `gymAdmin.nav.*` for the label. */
  labelKey: string;
  /**
   * A label per kind of place, where one name cannot serve both: the zones
   * editor configures a gym's climbing area and a crag's sectors. Falls back to
   * `labelKey`, which is what nearly every entry uses.
   */
  labelKeys?: Partial<Record<ManagedLocationType, string>>;
  icon: LucideIcon;
  roles: StaffRole[];
  /**
   * Which kinds of location the entry belongs to. A crag has no opening hours,
   * no members, no walls to scan and no boards — it gets its profile and its
   * sectors, and the rest of the menu is a gym's.
   */
  locations: ManagedLocationType[];
  /**
   * A further test on the shape of the place, for the few entries a kind alone
   * cannot decide: a stage and a gym's event are both `competition`, and only
   * one of them keeps categories. Absent means the kind is enough.
   */
  when?: (place: PlaceShape) => boolean;
  /**
   * Rows under the entry that are records rather than sections, and so are
   * fetched rather than declared here: the competition's rounds, one scoreboard
   * each. This file still says which entry has them — the sidebar knows how to
   * get them and what to draw.
   */
  subMenu?: 'rounds';
}

/**
 * A block of entries under one header. The headers are not pages: they name
 * the three sides of the job — the place and how it is set up, the people it
 * speaks to, and the climbing on the walls — so that a menu this long can be
 * read at a glance.
 */
export interface GymAdminNavSection {
  /** Stable id: the sidebar's React key, and what the tests name. */
  id: string;
  /**
   * The header, per kind of place: the same block is a gym's "Gym" and a crag's
   * "Crag".
   */
  labelKeys: Record<ManagedLocationType, string>;
  /**
   * The header for a competition that is nobody's stage, where the stage's
   * would name a circuit that is not there. Only the block that names the
   * place needs one.
   */
  eventLabelKeys?: Partial<Record<ManagedLocationType, string>>;
  items: GymAdminNavItem[];
}

const GYM_ONLY: ManagedLocationType[] = ['gym'];
const CRAG_ONLY: ManagedLocationType[] = ['crag'];
const TROPHY_ONLY: ManagedLocationType[] = ['trophy'];
const COMPETITION_ONLY: ManagedLocationType[] = ['competition'];
/** Whoever keeps categories and teams: a circuit, or an event of nobody's circuit. */
const SEASON_KEEPERS: ManagedLocationType[] = ['trophy', 'competition'];
/** What has sponsors under its cover: a circuit, and any competition. */
const SPONSORED: ManagedLocationType[] = ['trophy', 'competition'];
/** Everywhere: the four all have an identity and a team. */
const EVERYWHERE: ManagedLocationType[] = ['gym', 'crag', 'trophy', 'competition'];
/** Anywhere climbs go up on a wall — which is not a crag's topo, nor a circuit. */
const WALLS: ManagedLocationType[] = ['gym', 'competition'];
/**
 * A stage's problems are numbered blocks set on the day and scored from the
 * round's own board — it has no climbs of its own to lay out, credit or move
 * about, so the walls entries a gym's own event still keeps are noise on a
 * stage.
 */
const notAStage = (place: PlaceShape) => !place.stage;
/**
 * Anywhere we hold people's data on somebody else's behalf — a gym's members, a
 * circuit's entrants. What the Art. 28 agreement is about.
 */
const HOLDS_PERSONAL_DATA: ManagedLocationType[] = ['gym', 'trophy'];
/** Anywhere with climbs of its own to lay out and credit. Not a circuit. */
const CLIMBING_PLACES: ManagedLocationType[] = ['gym', 'crag', 'competition'];

export const GYM_ADMIN_NAV_SECTIONS: GymAdminNavSection[] = [
  // The place and how it is set up: who it is, when it opens, who works there,
  // who issues the cards its members carry, what hardware hangs on its walls
  // and how the climbing area is laid out. Everything here is settled once and
  // revisited rarely, which is what separates it from the walls below.
  {
    id: 'place',
    labelKeys: {
      gym: 'gymAdmin.nav.sections.gym',
      crag: 'gymAdmin.nav.sections.crag',
      trophy: 'gymAdmin.nav.sections.trophy',
      competition: 'gymAdmin.nav.sections.stage',
    },
    eventLabelKeys: { competition: 'gymAdmin.nav.sections.event' },
    items: [
      { path: '/info', labelKey: 'gymAdmin.nav.info', icon: Building2, roles: ['admin'], locations: EVERYWHERE },
      { path: '/hours', labelKey: 'gymAdmin.nav.hours', icon: Clock, roles: ['admin', 'editor'], locations: GYM_ONLY },
      { path: '/staff', labelKey: 'gymAdmin.nav.staff', icon: Users, roles: ['admin'], locations: EVERYWHERE },
      // The bodies a membership card can be issued by: reference data about the
      // gym rather than about any one member, which is why it sits here and not
      // with the roster it feeds.
      {
        path: '/members/organizations',
        labelKey: 'gymAdmin.nav.membershipOrganizations',
        icon: Landmark,
        roles: ['admin', 'member_manager'],
        locations: GYM_ONLY,
      },
      // The classes and the squads the circuit runs. Reference data about the
      // trophy rather than about any one entrant, which is why they sit here
      // rather than with the people they sort. A gym's own event has no
      // circuit to borrow them from and keeps its own; a stage never does.
      //
      // The squads are also what an athlete turns up with and what the team
      // managers are named on, so the desk reaches them too: whoever types the
      // entries is who meets a squad nobody has written down yet.
      {
        path: '/categories',
        labelKey: 'gymAdmin.nav.categories',
        icon: Layers,
        roles: ['admin'],
        locations: SEASON_KEEPERS,
        when: (place) => !place.stage,
      },
      {
        path: '/teams',
        labelKey: 'gymAdmin.nav.teams',
        icon: Shield,
        roles: ['admin', 'member_manager'],
        locations: SEASON_KEEPERS,
        when: (place) => !place.stage,
      },
      // Who speaks for each of those squads *here*. On the competition rather
      // than the circuit because an appointment is for one weekend — so this one
      // shows on a stage, where /teams does not, as well as on an event of its
      // own. Admin-only for a stronger reason than the rest of the block: naming
      // a team manager hands somebody the right to write on other people's cards.
      {
        path: '/team-managers',
        labelKey: 'gymAdmin.nav.teamManagers',
        icon: ClipboardList,
        roles: ['admin'],
        locations: COMPETITION_ONLY,
      },
      // Admin-only: a device's code is its read credential on the public
      // endpoint, so it sits with admins rather than the wider editor role.
      { path: '/devices', labelKey: 'gymAdmin.nav.devices', icon: HardDrive, roles: ['admin'], locations: GYM_ONLY },
      // Zones and the suggested climb names: the setup the whole climbing area
      // is described against. Outdoors the same page is the sectors, so it
      // takes the crag's own name — under a "Crag" header, "Climbing area
      // setup" would be naming a room that is not there. A competition held at
      // a gym is climbed in the gym's rooms and has nothing of its own to set
      // up: that is done from the gym. A stage has no climbs to describe at all.
      {
        path: '/route-setting',
        labelKey: 'gymAdmin.nav.configuration',
        labelKeys: { crag: 'gymAdmin.nav.cragConfiguration' },
        icon: Settings2,
        roles: ['admin', 'editor', 'routesetter'],
        locations: CLIMBING_PLACES,
        when: (place) => !place.hostedAtVenue && notAStage(place),
      },
      // Who backs the circuit or the event: the strip of logos under its cover
      // (issue #374). Last in the block on purpose — the first entry somebody
      // may open is where the panel lands them, and nobody should land here on
      // competition day. Admins only: a trophy or a competition has no editors.
      {
        path: '/sponsors',
        labelKey: 'gymAdmin.nav.sponsors',
        icon: Handshake,
        roles: ['admin'],
        locations: SPONSORED,
      },
    ],
  },
  // The stages of a circuit, and the events a gym holds. Its own block rather
  // than an entry in the one above, because for a trophy this *is* the panel —
  // everything else about a circuit is settled once and this is the work.
  {
    id: 'stages',
    labelKeys: {
      gym: 'gymAdmin.nav.sections.events',
      crag: 'gymAdmin.nav.sections.stages',
      trophy: 'gymAdmin.nav.sections.stages',
      competition: 'gymAdmin.nav.sections.stages',
    },
    items: [
      {
        path: '/stages',
        labelKey: 'gymAdmin.nav.stages',
        icon: Flag,
        roles: ['admin'],
        locations: TROPHY_ONLY,
      },
      // The competitions held at the gym: its own, and the stages it hosts for
      // somebody's circuit. Same list, same card, the gym's side of the link.
      {
        path: '/events',
        labelKey: 'gymAdmin.nav.events',
        icon: Flag,
        roles: ['admin'],
        locations: GYM_ONLY,
      },
    ],
  },
  // The people the place exists for, and what it says to them.
  {
    id: 'community',
    labelKeys: {
      gym: 'gymAdmin.nav.sections.community',
      crag: 'gymAdmin.nav.sections.community',
      trophy: 'gymAdmin.nav.sections.community',
      competition: 'gymAdmin.nav.sections.entrants',
    },
    items: [
      // A gym's own books. A circuit keeps none — its people are its entrants,
      // which is the entry below.
      {
        path: '/members',
        labelKey: 'gymAdmin.nav.members',
        icon: IdCard,
        roles: ['admin', 'member_manager'],
        locations: GYM_ONLY,
      },
      // Everybody taking part in the circuit, gathered across its stages. One
      // row per person, so a tax code is corrected once rather than six times.
      {
        path: '/participants',
        labelKey: 'gymAdmin.nav.trophyParticipants',
        icon: IdCard,
        roles: ['admin', 'member_manager'],
        locations: TROPHY_ONLY,
      },
      // The competition's rounds. The entry has no list of its own: every round
      // hangs under it as a row, each opening that round's board — on the day
      // that board is the panel, and it has no business being two pages down
      // from the menu. Making and unmaking a round is on the menu too.
      //
      // The board reads either way round — by athlete or by block — from the
      // switch on the round's own page, so there is no second menu entry for
      // the transposed table.
      //
      // Filling a round in as it happens stays in the member app: that is done
      // in the room, on a phone, by whoever is standing at the wall. Which is
      // why no entry here is a judge's: they score from app.divepeaks, and the
      // panel is for the people who run the competition.
      {
        path: '/rounds',
        labelKey: 'gymAdmin.nav.rounds',
        icon: ListOrdered,
        roles: ['admin', 'editor'],
        locations: COMPETITION_ONLY,
        subMenu: 'rounds',
      },
      // Each class's standings across the rounds it climbs — its turns ranked
      // as one table, a final on its own. Right under the rounds' rows, with
      // the same roles: it is read off their cards, live, by the people who
      // run them.
      {
        path: '/standings',
        labelKey: 'gymAdmin.nav.standings',
        icon: Medal,
        roles: ['admin', 'editor'],
        locations: COMPETITION_ONLY,
      },
      // One stage's entry list, drawn from the circuit's roster above.
      {
        path: '/registrations',
        labelKey: 'gymAdmin.nav.registrations',
        icon: ClipboardList,
        roles: ['admin', 'member_manager'],
        locations: COMPETITION_ONLY,
      },
      // Right below the people it governs: the Art. 28 agreement is the reason
      // a gym's roster or a circuit's entrants can be served at all, and an
      // admin sent here from a blocked page should find it where they were
      // already looking.
      {
        path: '/dpa',
        labelKey: 'gymAdmin.nav.dpa',
        icon: FileSignature,
        roles: ['admin'],
        locations: HOLDS_PERSONAL_DATA,
      },
      {
        path: '/announcements',
        labelKey: 'gymAdmin.nav.announcements',
        icon: Megaphone,
        roles: ['admin', 'editor'],
        locations: GYM_ONLY,
      },
    ],
  },
  // The climbing itself — the work that starts over every time a wall is reset.
  // Outdoors the same block is the sectors: a crag has no wall to photograph
  // and no hardware, so what is left is where the routes are and who looks
  // after them.
  {
    id: 'walls',
    labelKeys: {
      gym: 'gymAdmin.nav.sections.climbingArea',
      crag: 'gymAdmin.nav.sections.sectors',
      trophy: 'gymAdmin.nav.sections.climbingArea',
      competition: 'gymAdmin.nav.sections.climbingArea',
    },
    items: [
      // Wall scans and the climbs made out of them. Both climb roles: an editor
      // already creates climbs by hand, and a route setter is the one who was
      // standing at the wall that got photographed.
      {
        path: '/scans',
        labelKey: 'gymAdmin.nav.scans',
        icon: ScanLine,
        roles: ['admin', 'editor', 'routesetter'],
        locations: GYM_ONLY,
      },
      // Who this place credits with setting: the list the setter picker opens
      // on. Not admin-only, unlike /staff — outdoors this is the register of who
      // bolts here, and keeping it current is the setters' own job rather than
      // the owner's.
      {
        path: '/route-setters',
        labelKey: 'gymAdmin.nav.routeSetters',
        icon: HardHat,
        roles: ['admin', 'routesetter'],
        locations: CLIMBING_PLACES,
        when: notAStage,
      },
      // The crag's topo: its routes, drawn on the sectors' photos. A gym's
      // equivalent is the scans page plus the kanban, both of which start from a
      // wall photograph — outdoors the sector's own picture is the only one
      // there is, so the two jobs are one screen.
      {
        path: '/crag-routes',
        labelKey: 'gymAdmin.nav.cragRoutes',
        icon: Spline,
        roles: ['admin', 'editor', 'routesetter'],
        locations: CRAG_ONLY,
      },
      // The report queue. Accepting one forwards it by email to the crag's
      // maintainers in the crag's name, so it stays with the roles that already
      // speak for the place — not with route setters, who move climbs.
      {
        path: '/reports',
        labelKey: 'gymAdmin.nav.reports',
        icon: TriangleAlert,
        roles: ['admin', 'editor'],
        locations: CRAG_ONLY,
      },
      // The two kanban dashboards. Narrower than the scans above: the boards
      // move and delete climbs, which is the setters' and admins' call, not an
      // editor's.
      {
        path: '/boulders-board',
        labelKey: 'gymAdmin.nav.bouldersBoard',
        icon: Blocks,
        roles: ['admin', 'routesetter'],
        locations: WALLS,
        when: notAStage,
      },
      {
        path: '/routes-board',
        labelKey: 'gymAdmin.nav.routesBoard',
        icon: Waypoints,
        roles: ['admin', 'routesetter'],
        locations: WALLS,
        when: notAStage,
      },
      { path: '/boards', labelKey: 'gymAdmin.nav.boards', icon: Grid3x3, roles: ['admin', 'editor'], locations: GYM_ONLY },
    ],
  },
];

/** A section with its header resolved and only the entries this user may open. */
export interface AllowedNavSection {
  id: string;
  /** i18n key for the header, already picked for this kind of place. */
  labelKey: string;
  items: GymAdminNavItem[];
}

function isAllowed(
  item: GymAdminNavItem,
  roles: StaffRole[],
  locationType: ManagedLocationType,
  place: PlaceShape
): boolean {
  return (
    item.locations.includes(locationType) &&
    item.roles.some((role) => roles.includes(role)) &&
    (item.when?.(place) ?? true)
  );
}

/** The entry as it reads at this kind of place, for the few that read differently. */
function labelledFor(item: GymAdminNavItem, locationType: ManagedLocationType): GymAdminNavItem {
  const labelKey = item.labelKeys?.[locationType];
  return labelKey ? { ...item, labelKey } : item;
}

/** The header as it reads here: a stage's block is an event's for an event. */
function sectionLabelFor(
  section: GymAdminNavSection,
  locationType: ManagedLocationType,
  place: PlaceShape
): string {
  return (
    (place.stage ? undefined : section.eventLabelKeys?.[locationType]) ??
    section.labelKeys[locationType]
  );
}

/**
 * The menu as the sidebar draws it: the sections visible to a user holding the
 * given roles at a place of the given kind and shape, empty ones dropped and
 * every label resolved. All three filters apply to every entry: a crag admin
 * holds every role there is and still has no opening hours to set.
 */
export function allowedNavSections(
  roles: StaffRole[],
  locationType: ManagedLocationType = 'gym',
  place: PlaceShape = PLAIN_PLACE
): AllowedNavSection[] {
  return GYM_ADMIN_NAV_SECTIONS.map((section) => ({
    id: section.id,
    labelKey: sectionLabelFor(section, locationType, place),
    items: section.items
      .filter((item) => isAllowed(item, roles, locationType, place))
      .map((item) => labelledFor(item, locationType)),
  })).filter((section) => section.items.length > 0);
}

/**
 * The same entries, flattened in menu order. This is what the guards ask, and
 * `[0]` is the section a user lands on when they open the panel.
 */
export function allowedNavItems(
  roles: StaffRole[],
  locationType: ManagedLocationType = 'gym',
  place: PlaceShape = PLAIN_PLACE
): GymAdminNavItem[] {
  return allowedNavSections(roles, locationType, place).flatMap((section) => section.items);
}

/**
 * Every first segment a path inside the panel can start with — the menu's
 * entries plus the one page that is not in the menu. `/members/9/edit` and
 * `/members/organizations` both begin with `members`.
 */
const SECTION_SEGMENTS = new Set([
  ...GYM_ADMIN_NAV_SECTIONS.flatMap((section) => section.items).map(
    (item) => item.path.split('/')[1]
  ),
  'no-access',
]);

/**
 * Whether a URL's first segment names a section rather than a place. That is
 * how a link from before the place was in the URL is told apart from a slug:
 * `/info` and `/members/9/edit` want a place put in front of them, `/junglerock`
 * is one.
 */
export function isSectionSegment(segment: string): boolean {
  return SECTION_SEGMENTS.has(segment);
}

/** Whether a path is one this user may open here. */
export function isNavPathAllowed(
  path: string,
  roles: StaffRole[],
  locationType: ManagedLocationType = 'gym',
  place: PlaceShape = PLAIN_PLACE
): boolean {
  return allowedNavItems(roles, locationType, place).some((item) => item.path === path);
}

/**
 * The entry a path belongs to, whatever section it sits in — the page title.
 * Takes the kind of place for the same reason the sidebar does: one page is
 * the climbing area's setup indoors and the sectors' outdoors.
 *
 * A page drilled into from a section is named by that section: `/rounds/12`
 * and `/rounds/12/edit` are both under "rounds", and a record's own page
 * saying nothing at all is worse than saying where it sits. The exact path
 * still wins, so `/members/organizations` keeps its own name.
 */
export function navItemByPath(
  path: string,
  locationType: ManagedLocationType = 'gym'
): GymAdminNavItem | undefined {
  const entries = GYM_ADMIN_NAV_SECTIONS.flatMap((section) => section.items);
  const root = sectionRoot(path);
  const item =
    entries.find((candidate) => candidate.path === path) ??
    entries.find((candidate) => candidate.path === root);

  return item && labelledFor(item, locationType);
}
